import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isIgnored } from "~/lib/pipeline";
import { FilterByOption } from "~/components/build/Show/lib/filterConstants";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useFilterStore } from "~/components/build/Show/lib/useFilterStore";
import { withoutDrawerParams } from "~/components/build/Show/lib/urlForView";

/**
 * Toggle the shared "show skipped steps" preference (used by both the list and
 * canvas views).
 *
 * When skipped steps are being hidden we also:
 *  - clear the skipped filter, otherwise the filter option disappears from the
 *    UI while still being active, leaving users with a filter they can't see or
 *    deselect; and
 *  - deselect the current step if it's skipped, since leaving a now-hidden
 *    step selected (with its drawer open) is confusing.
 */
export function useToggleSkippedSteps(): () => void {
  const showSkippedSteps = useBuildPreferencesStore((state) => state.showSkippedSteps);
  const setShowSkippedSteps = useBuildPreferencesStore((state) => state.setShowSkippedSteps);
  const setFilterBy = useFilterStore((state) => state.setFilterBy);
  const { currentStep } = useCurrentJobOrStep();
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(() => {
    if (!showSkippedSteps) {
      setShowSkippedSteps(true);
      return;
    }

    setShowSkippedSteps(false);
    setFilterBy(
      useFilterStore.getState().filterBy.filter((filter) => filter !== FilterByOption.Ignored),
    );

    if (currentStep != null && isIgnored(currentStep)) {
      const params = withoutDrawerParams(new URLSearchParams(location.search));
      const qs = params.toString();
      navigate(qs ? `${location.pathname}?${qs}` : location.pathname);
    }
  }, [
    showSkippedSteps,
    setShowSkippedSteps,
    setFilterBy,
    currentStep,
    navigate,
    location.pathname,
    location.search,
  ]);
}
